import {
  AmountField,
  countryName,
  Dialog,
  InvalidInput,
  updateCar,
  type Plan,
} from '@trippath/shared';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { problemKey } from '../../lib/format';

/** A country's price of a litre as typed, beside the one shown when the dialog opened. */
type Price = Plan['car']['prices'][number] & { typed: number | null };

interface Props {
  plan: Plan;
  onClose: () => void;
  onSaved: (plan: Plan) => void;
}

/** What a petrol car uses unless said, and an electric one, in tenths per 100 km. */
const USE = 70;
const ELECTRIC_USE = 170;

/** Litres per 100 km as typed, `6.5` or `6,5`, in tenths; `null` for anything else. */
export function tenthsOf(text: string): number | null {
  const found = /^(\d{1,2})(?:[.,](\d))?$/.exec(text.trim());
  if (found === null) return null;
  const [, whole = '', tenth = '0'] = found;
  const tenths = Number(whole) * 10 + Number(tenth);
  return tenths >= 10 && tenths <= 500 ? tenths : null;
}

/**
 * What the trip's car uses and what a litre costs in each country of the route — the typical
 * pump price until the person says theirs — after which each way by car not bought yet is
 * priced again.
 */
export function CarDialog({ plan, onClose, onSaved }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const field = useId();
  const { car } = plan;
  const shown = (tenths: number) =>
    (tenths / 10).toLocaleString(lang, { maximumFractionDigits: 1 });
  const [electric, setElectric] = useState(car.electric);
  const [own, setOwn] = useState(car.own);
  const [use, setUse] = useState(shown(car.useTenths));
  const [prices, setPrices] = useState<Price[]>(() =>
    car.prices.map((p) => ({ ...p, typed: p.perLitre.minor })),
  );
  // Another kind of car: what such a car uses, and the typical prices until the server says.
  const runOn = (next: boolean) => {
    if (next === electric) return;
    setElectric(next);
    setUse(shown(next ? ELECTRIC_USE : USE));
    setPrices((was) => was.map((p) => ({ ...p, own: false, typed: null })));
  };
  const [failed, setFailed] = useState<string | null>(null);
  const tenths = tenthsOf(use);

  const save = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (tenths === null) return;
    // A price is the trip's own once it differs from the one shown, or was its own before.
    const kept = prices.flatMap(({ country, typed, own: was, perLitre }) =>
      typed !== null && (was || typed !== perLitre.minor || electric !== car.electric)
        ? [{ country, perLitre: typed }]
        : [],
    );
    setFailed(null);
    updateCar(plan.trip.id, { electric, own, useTenths: tenths }, kept).then(
      onSaved,
      (error: unknown) => {
        const [first] = error instanceof InvalidInput ? error.fields : [];
        setFailed(t(first ? problemKey(first.problem) : 'plan.failed'));
      },
    );
  };

  return (
    <Dialog open title={t('car.title')} onClose={onClose}>
      <form className="form" onSubmit={save} noValidate>
        {/* Hired, a rental to book for a way by car; or the traveller's own. */}
        <div className="field">
          <span className="field-label" id={`${field}-whose`}>
            {t('car.whose')}
          </span>
          <div className="segmented" role="group" aria-labelledby={`${field}-whose`}>
            {[false, true].map((option) => (
              <button
                key={String(option)}
                type="button"
                aria-pressed={own === option}
                onClick={() => {
                  setOwn(option);
                }}
              >
                {t(option ? 'car.own' : 'car.hired')}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <span className="field-label" id={`${field}-kind`}>
            {t('car.kind')}
          </span>
          <div className="segmented" role="group" aria-labelledby={`${field}-kind`}>
            {[false, true].map((option) => (
              <button
                key={String(option)}
                type="button"
                aria-pressed={electric === option}
                onClick={() => {
                  runOn(option);
                }}
              >
                {t(option ? 'car.electric' : 'car.petrol')}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label className="field-label" htmlFor={field}>
            {t(electric ? 'car.useElectric' : 'car.use')}
          </label>
          <input
            id={field}
            className="text-input"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={use}
            aria-invalid={tenths === null}
            aria-describedby={tenths === null ? `${field}-wrong` : undefined}
            onChange={(event) => {
              setUse(event.target.value);
            }}
          />
          {tenths === null && (
            <p id={`${field}-wrong`} className="field-error">
              {t('car.useWrong')}
            </p>
          )}
        </div>
        {prices.map((p) => (
          <AmountField
            key={`${p.country}-${String(electric)}`}
            label={t(electric ? 'car.priceElectric' : 'car.price', {
              country: countryName(p.country, lang),
            })}
            value={p.typed}
            currency={p.perLitre.currency}
            onChange={(minor) => {
              setPrices((was) =>
                was.map((q) => (q.country === p.country ? { ...q, typed: minor } : q)),
              );
            }}
          />
        ))}
        {failed !== null && (
          <p className="field-error" role="alert">
            {failed}
          </p>
        )}
        <div className="dialog-actions">
          <button type="button" className="button" onClick={onClose}>
            {t('dialog.cancel')}
          </button>
          <button type="submit" className="button button-primary">
            {t('car.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
